/* eslint-disable @typescript-eslint/no-explicit-any */
import { Capacitor } from "@capacitor/core";
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonPage,
  IonSpinner,
  IonToolbar,
} from "@ionic/react";
import _ from "lodash";
import { CenterContainer, CommentsCard, HeaderTitle } from "parafolio-components";
import React, { useEffect, useState } from "react";
import { EventRegister } from "react-native-event-listeners";
import { useHistory, useParams } from "react-router";
import { useRecoilValueLoadable } from "recoil";
import { Icon_Add_Evidence, Icon_Refresh } from "@assets/images";
import ProgramInfoCard from "@components/home/ProgramInfoCard";
import EvidenceDisclaimer from "@components/programs/EvidenceDisclaimer";
import ProgramCardButton from "@components/programs/ProgramCardButton";
import ProgramEvidenceCard from "@components/programs/ProgramEvidenceCard";
import ProgressCheckDescription from "@components/programs/ProgressCheckDescription";
import SubmitProgressCheckModal from "@components/programs/SubmitProgressCheckModal";
import DataController from "@controllers/DataController";
import * as ProgressCheckService from "@controllers/ProgressCheckService";
import { useProgramData } from "@hooks/data/useProgramData";
import {
  AllProgressCheckStatuses,
  CompetenceActivityClass,
  FilledInBy,
  IEvidence,
  IUser,
  LearningObjective,
  ProgressCheck,
  ProgressCheckApproval,
  ProgressCheckStatus,
  SubmissionState,
} from "@interfaces/Interfaces";
import { evidenceAtom, progressCheckDataAtom, userAtom } from "@state/State";
import * as CommentHelpers from "@utils/commentHelpers";
import * as EvidenceUtils from "@utils/evidenceUtils";
import * as ProgramUtils from "@utils/programUtils";
import ProgressCheckEvidenceButton, { CompetenceStatus } from "@components/programs/ProgressCheckEvidenceButton";
import MonthlyEvidenceBreakdown from "@components/evidence/MonthlyEvidenceBreakdown";
import { useRefreshProgressCheckData } from "@hooks/data/useRefreshProgressCheckData";
import { useProgramModuleStatus } from "@hooks/programs/useProgramModuleStatus";
import { COMPETENCY_IDS, PROGRAM_IDS, PROGRESS_CHECK_IDS } from "@constants/programs";
import { useRefreshProgramData } from "@hooks/programs/useRefreshProgramData";
import { useProgramCycles } from "@hooks/programs/useProgramCycles";

const ProgressCheckPage: React.FC = () => {
  const history = useHistory();
  const params = useParams<{ programId: string; progressCheckId: string }>();
  const { userPrograms } = useProgramData();

  const { currentProgramCycle } = useProgramCycles(params.programId);
  const { refreshProgressCheckData } = useRefreshProgressCheckData();
  const { isRefreshingProgramData, refreshUserProgramData } = useRefreshProgramData();
  const { fastTrackActive, pauseAndHold } = useProgramModuleStatus(params.programId);

  const user = useRecoilValueLoadable<IUser | null>(userAtom);
  const evidence = useRecoilValueLoadable<IEvidence[] | null>(evidenceAtom);
  const allProgressCheckData = useRecoilValueLoadable<AllProgressCheckStatuses[]>(progressCheckDataAtom);

  const program = userPrograms.find((item) => item.ID === params.programId)!;
  const [progressCheck, setProgressCheck] = useState<ProgressCheck | any | null>(null);
  const [progressCheckEvidence, setProgressCheckEvidence] = useState<IEvidence[]>([]);
  const [progressCheckCycleEvidence, setProgressCheckCycleEvidence] = useState<IEvidence[] | undefined>(undefined);
  const [competences, setCompetences] = useState<CompetenceActivityClass[]>([]);
  const [allEvidenceAdded, setAllEvidenceAdded] = useState<boolean>(false);
  const [progressChecks, setProgressChecks] = useState<ProgressCheck[]>([]);

  const [currentProgressCheckData, setCurrentProgressCheckData] = useState<AllProgressCheckStatuses | null>(null);
  const [progressCheckData, setProgressCheckData] = useState<ProgressCheckStatus | null>(null);

  const [submitProgressCheckModalVisible, setSubmitProgressCheckModalVisible] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    const getProgramData = (): void => {
      if (program) {
        const allProgressChecks: ProgressCheck[] = program.ProgressChecks ?? [];

        const _progressCheck: ProgressCheck | undefined = allProgressChecks.find(
          (item: any): item is ProgressCheck => item.ID === params.progressCheckId
        );

        setProgressChecks(allProgressChecks);

        if (_progressCheck) {
          setProgressCheck(_progressCheck);

          if (_progressCheck["Competence/Activity"]) {
            if (fastTrackActive) {
              setCompetences(_progressCheck["Competence/Activity"]);
            } else {
              const _competences = _progressCheck["Competence/Activity"].filter((item) => !item.FastTrackEvidence);
              setCompetences(_competences);
            }
          }
        }
      }
    };

    getProgramData();
  }, [program?.version, fastTrackActive]);

  useEffect(() => {
    const getProgressCheckData = (): void => {
      const dataForProgram: AllProgressCheckStatuses = allProgressCheckData.contents?.find(
        (item: any): item is AllProgressCheckStatuses => item.programID === program?.ID
      );

      const dataForProgressCheck = dataForProgram?.pCs.find((item) => item.pCId === progressCheck?.ID);

      dataForProgram && setCurrentProgressCheckData(dataForProgram);
      dataForProgressCheck && setProgressCheckData(dataForProgressCheck);
    };

    if (allProgressCheckData.state === "hasValue") {
      getProgressCheckData();
    }
  }, [allProgressCheckData, program, progressCheck]);

  useEffect(() => {
    const getEvidenceForProgressCheck = (): void => {
      let evidenceForProgressCheck: IEvidence[];

      evidenceForProgressCheck = evidence.contents?.filter((evidence: IEvidence) => {
        const evidenceJSON = evidence.evidenceJSON;
        const id = evidenceJSON?.programInfo?.progressCheckID || "";

        return id === progressCheck?.ID;
      });

      evidenceForProgressCheck = _.orderBy(evidenceForProgressCheck, "date", "desc");

      setProgressCheckEvidence(evidenceForProgressCheck);

      if (currentProgramCycle) {
        const evidenceForCycle = evidenceForProgressCheck.filter((item) => {
          return item.evidenceJSON?.programInfo?.programCycle === currentProgramCycle;
        });

        setProgressCheckCycleEvidence(evidenceForCycle);
      }
    };

    if (evidence.state === "hasValue" && progressCheck) {
      getEvidenceForProgressCheck();
    }
  }, [evidence, progressCheck]);

  useEffect(() => {
    const checkAllEvidenceAddedForProgressCheck = (): void => {
      let evidenceForProgressCheck: IEvidence[] = _.cloneDeep(progressCheckEvidence);

      evidenceForProgressCheck = evidenceForProgressCheck.filter((item) => {
        const evidenceJSON = item.evidenceJSON;

        const onHoldReset = evidenceJSON?.OnHoldReset === 1;

        return !onHoldReset && item.draft !== true;
      });

      const allCompetences = _.cloneDeep(competences);

      let allEvidenceAdded = false;

      if (ProgramUtils.canHandleMultiplePiecesOfEvidencePerEvidenceType(program.ID)) {
        if (ProgramUtils.canSubmitProgressCheck(progressCheck.Approval)) {
          const progressCheckIDs = evidenceForProgressCheck.map((item: IEvidence) => {
            const evidenceJSON = item.evidenceJSON;

            return evidenceJSON?.programInfo?.progressCheckCompetenceID;
          });

          let requiredCompetences = allCompetences.filter(
            (competence: CompetenceActivityClass) =>
              competence["Number Evidences required"] && competence["Number Evidences required"] > 0
          );

          if (program.ID === PROGRAM_IDS.NWAS_NQP) {
            requiredCompetences = requiredCompetences.filter((competence: CompetenceActivityClass) => {
              return competence.FilledInBy !== FilledInBy.Mentor;
            });
          }

          const competenceIDs = requiredCompetences.map((item) => item.ID).sort();
          allEvidenceAdded = competenceIDs.every((id) => progressCheckIDs.includes(id));
        }
      } else {
        const progressCheckIDs = evidenceForProgressCheck.map((item: IEvidence) => {
          const evidenceJSON = item.evidenceJSON;

          return evidenceJSON?.programInfo?.progressCheckCompetenceID;
        });
        const competenceIDs = allCompetences.map((item) => item.ID).sort();
        allEvidenceAdded = competenceIDs.every((id) => progressCheckIDs.includes(id));
      }

      setAllEvidenceAdded(allEvidenceAdded);
    };

    if (progressCheckEvidence.length > 0) {
      checkAllEvidenceAddedForProgressCheck();
    } else {
      setAllEvidenceAdded(false);
    }
  }, [competences, progressCheckEvidence]);

  const addEvidencePressed = (competence?: CompetenceActivityClass): void => {
    if (competence?.isWellbeingActivity) {
      const programID = program?.ID;
      const progressCheckID = progressCheck?.ID;
      const competenceID = competence?.ID;

      history.push(
        `/dashboard/program/${programID}/progress-check/${progressCheckID}/wellbeing-activity/${competenceID}`
      );
    } else {
      const progressChecks = program.ProgressChecks ?? [];
      let progressCompetence = competence;
      const skills = ProgramUtils.getSkillsForRole(user.contents, program);

      if (!progressCompetence && progressCheck?.["Competence/Activity"].length === 1) {
        progressCompetence = progressCheck?.["Competence/Activity"][0];
      }

      const preselectedSkill = ProgramUtils.getPreselectedSkillForCompetence(progressCompetence, skills);

      const data = {
        program: program,
        // progressCompetences: competences, // commented out because these were overwriting removeProgressCheckCompetences() behaviour
        ...(progressCompetence && { progressCompetence }),
        progressCheck: progressCheck,
        ...(progressChecks.length > 0 && { progressChecks }),
        ...(skills.length > 0 && { skills }),
        learningOutcomes: competence?.["Learning Objective"] ?? [],
        disableProgressCheckDropdown: true,
        disableProgressCheckCompetenceDropdown: progressCompetence !== undefined,
        ...(preselectedSkill && { preselectedSkill }),
      };

      EventRegister.emit("evidence/add-progress-check-evidence", data);
    }
  };

  const goToComments = (): void => {
    const programID = program?.ID;
    const progressCheckID = progressCheck?.ID;

    history.push(`/dashboard/program/${programID}/progress-check/${progressCheckID}/comments`);
  };

  const evidenceCardPressed = (item: IEvidence): void => {
    const _evidence = _.pickBy(_.cloneDeep(item), _.identity);

    const evidenceJSON = _evidence.evidenceJSON;
    const progressCheckCompetenceID = evidenceJSON?.programInfo?.progressCheckCompetenceID;

    const programID = program?.ID;
    const progressCheckID = progressCheck?.ID;

    const competence = competences.find((item: CompetenceActivityClass) => item.ID === progressCheckCompetenceID);

    if (competence?.isWellbeingActivity) {
      history.push(
        `/dashboard/program/${programID}/progress-check/${progressCheckID}/wellbeing-activity/${progressCheckCompetenceID}/results/${_evidence.id}`
      );
    } else {
      history.push(`/dashboard/program/${programID}/progress-check/${progressCheckID}/read/${_evidence.id}`);
    }
  };

  const submitProgressCheck = async (): Promise<void> => {
    try {
      setSubmitting(true);
      let submitted = false;

      submitted = await ProgressCheckService.updateProgressCheck(user.contents, program?.ID, progressCheck?.ID);
      submitted && (await refreshProgressCheckData(user.contents));

      setSubmitting(false);
      setSubmitProgressCheckModalVisible(false);
    } catch (error) {
      console.log(error);
      setSubmitting(false);
    }
  };

  const submitButtonPressed = (): void => {
    setSubmitProgressCheckModalVisible(true);
  };

  const getSubmissionCardHeaderText = (progressCheck: ProgressCheck, data: ProgressCheckStatus | null): string => {
    if (data) {
      if (data.submissions.status === SubmissionState.Submitted) {
        return progressCheck.SubmissionWaitingText ?? "";
      } else if (data.submissions.status === SubmissionState.OnHoldReset) {
        return progressCheck.SubmissionRejectionResetText ?? "";
      } else if (data.submissions.status === SubmissionState.OnHold) {
        return progressCheck.SubmissionRejectionText ?? "";
      }
    }

    return progressCheck.SubmissionHeaderText ?? "";
  };

  const getSubmissionSidePillText = (data: ProgressCheckStatus | null): string | undefined => {
    if (data) {
      if (data.submissions.status === SubmissionState.OnHoldReset) {
        return "Submission reset";
      } else if (data.submissions.status === SubmissionState.OnHold) {
        return "Submission held";
      }
    }

    return undefined;
  };

  const submissionHasError = (data: ProgressCheckStatus | null): boolean | undefined => {
    if (data) {
      if (data.submissions.status === SubmissionState.OnHoldReset) {
        return true;
      } else if (data.submissions.status === SubmissionState.OnHold) {
        return true;
      }
    }

    return undefined;
  };

  const getDateOfProgressCheckApproval = () => {
    let day = new Date().getDate();
    let month = new Date().toLocaleString("default", { month: "short" });
    let year = new Date().getFullYear();

    progressCheckEvidence
      .filter((e) => !e.draft)
      .forEach((evidence) => {
        const parsedEvidence = evidence.evidenceJSON;
        const onHoldReset = parsedEvidence.onHoldReset ?? 0;
        if (onHoldReset === 0 && parsedEvidence.progressCheckID === progressCheck.ID) {
          day = new Date(evidence.date).getDate();
          month = new Date(evidence.date).toLocaleString("default", { month: "short" });
          year = new Date(evidence.date).getFullYear();
        }
      });

    return `${day} ${month} ${year}`;
  };

  const getEvidenceLabel = (comp: CompetenceActivityClass): string | undefined => {
    if (!ProgramUtils.includeSubtitleInProgressCheckEvidenceButton(program.ID)) return;
    if (comp.hideCompetenceSubtitle) return;

    let label;

    let evidenceUploaded = 0;
    (progressCheckCycleEvidence ?? progressCheckEvidence)
      .filter((e) => !e.draft)
      .forEach((e) => {
        try {
          const parsedEvidence = e.evidenceJSON;
          const onHoldReset = parsedEvidence.OnHoldReset ?? 0;
          const rejected = parsedEvidence.programInfo?.approvalState === "Rejected";
          if (rejected) return;
          if (onHoldReset === 1) return;
          if (parsedEvidence?.programInfo?.progressCheckCompetenceID === comp.ID) {
            evidenceUploaded += 1;
          }
        } catch (error) {
          console.log(error);
        }
      });

    const evidenceRequired = comp["Number Evidences required"];

    if (evidenceUploaded === 0 && !evidenceRequired) {
      label = "Optional";
    } else {
      label = evidenceRequired ? `${evidenceUploaded}/${evidenceRequired} evidence` : `${evidenceUploaded} evidence`;
    }

    return label;
  };

  const getLearningOutcomesLabel = (comp: CompetenceActivityClass): string | undefined => {
    if (
      (program.RemoveSubtitleForManuallySelectedLOs || ProgramUtils.removeSubtitleForManuallySelectedLOs(program.ID)) &&
      !comp["LOs Automatic"]
    )
      return;

    if (!ProgramUtils.includeSubtitleInProgressCheckEvidenceButton(program.ID)) return;

    if (comp.hideLearningOutcomesCount) return;

    let label;

    // add learning outcomes suffix // TODO change for CHUB -- NEW
    if (comp["Learning Objective"] && comp["Learning Objective"]?.length) {
      // get los required
      const losRequired: LearningObjective[] = [];
      comp["Learning Objective"].forEach((currentLearningOutcome) => {
        const loFound = losRequired.find((lo) => lo.ID === currentLearningOutcome.ID);
        if (currentLearningOutcome.NumberRequired && !loFound) {
          losRequired.push(currentLearningOutcome);
        }
      });

      // get los done
      const filteredEvidence = progressCheckEvidence.filter((e) => {
        const parsed = e.evidenceJSON;
        return !e.draft && !parsed.OnHoldReset;
      });
      let losDone = 0;
      for (const lo of comp["Learning Objective"]) {
        if (ProgramUtils.isNQPProgram(program.ID)) {
          const evidenceForCompetence = filteredEvidence.filter((e) => {
            const parsedEvidence = e.evidenceJSON;
            return parsedEvidence.programInfo.progressCheckCompetenceID === comp.ID;
          });
          for (const evidence of evidenceForCompetence) {
            const parsedEvidence = evidence.evidenceJSON;
            if (parsedEvidence.LearningOutcomes) {
              Array.from(parsedEvidence.LearningOutcomes).forEach((outcome: any) => {
                if (outcome.ID === lo.ID) {
                  losDone += 1;
                }
              });
            }
            if (parsedEvidence.LearningObjectivesTickboxes) {
              Array.from(parsedEvidence.LearningObjectivesTickboxes.selectedOutcomes).forEach((outcome: any) => {
                if (outcome.id === lo.ID) {
                  losDone += 1;
                }
              });
            }
          }
        } else {
          const numRequired = !lo.NumberRequired || lo.NumberRequired === 0 ? 1 : lo.NumberRequired;
          let countOfLearningOutcomesDone = 0;
          for (const evidence of filteredEvidence) {
            const parsedEvidence = evidence.evidenceJSON;
            // los can appear in the "Comp Selector" or the "LearningOutcomes" key, so check both
            if (parsedEvidence["Comp Selector"]) {
              Array.from(parsedEvidence["Comp Selector"]).forEach((it: any) => {
                it.selectedOutcomes.forEach((outcome: any) => {
                  if (outcome.id === lo.ID) {
                    countOfLearningOutcomesDone += 1;
                  }
                });
              });
            }
            if (parsedEvidence["LearningOutcomes"]) {
              Array.from(parsedEvidence["LearningOutcomes"]).forEach((outcome: any) => {
                if (outcome.ID === lo.ID) {
                  countOfLearningOutcomesDone += 1;
                }
              });
            }
            if (parsedEvidence["LearningObjectivesTickboxes"]) {
              Array.from(parsedEvidence["LearningObjectivesTickboxes"].selectedOutcomes).forEach((outcome: any) => {
                if (outcome.id === lo.ID) {
                  countOfLearningOutcomesDone += 1;
                }
              });
            }
          }

          if (countOfLearningOutcomesDone >= numRequired) losDone += 1;
        }
      }

      if (losDone > 0 && comp["Learning Objective"]?.length > 0) {
        losDone = Math.min(losDone, comp["Learning Objective"].length);
      }

      if (ProgramUtils.removeMandatoryCountInProgressCheckEvidenceButton(params.programId)) {
        label = `${losDone}/${comp["Learning Objective"].length} Learning Outcomes`;
      } else {
        label = `${losDone}/${comp["Learning Objective"].length} Learning Outcomes (${losRequired.length} mandatory)`;
      }
    }

    return label;
  };

  const getCompetenceStatus = (comp: CompetenceActivityClass): CompetenceStatus => {
    let status: CompetenceStatus = "Open";

    const progressCheckLocked = ProgramUtils.checkIfProgressCheckIsLocked(
      progressCheck,
      progressChecks,
      evidence.contents,
      currentProgressCheckData,
      program.ID,
      fastTrackActive,
      pauseAndHold
    );

    if (
      ProgramUtils.isMTS(program.ID) ||
      ProgramUtils.isCHUB(program.ID) ||
      ProgramUtils.isClinicalAdvisor(program.ID)
    ) {
      if (
        progressCheckLocked ||
        ProgramUtils.isProgressCheckApprovedBasedOnCompTrigger(progressCheckEvidence, competences)
      ) {
        status = "Locked";
        return status;
      }
    } else {
      if (ProgramUtils.isUOCApprenticeship(program.ID)) {
        if (
          progressCheckData?.submissions.status === SubmissionState.Approved ||
          progressCheckData?.submissions.status === SubmissionState.Submitted
        ) {
          status = "Locked";
          return status;
        }

        const userLevel = ProgramUtils.getProgramLevel(user.contents?.programRoles ?? [], program.ID) ?? "";
        const uocLocked = ProgramUtils.isUOCProgramLockedByEoYReview(
          program.ID,
          progressChecks,
          allProgressCheckData.contents,
          evidence.contents ?? [],
          userLevel
        );
        if (uocLocked) {
          status = "Locked";
          return status;
        }
      }

      if (ProgramUtils.isNQPProgram(program.ID)) {
        const NQPProgramComplete = ProgramUtils.isProgramCompleteNQP(
          program.ProgressChecks,
          allProgressCheckData.contents,
          evidence.contents,
          program.ID
        );

        if (NQPProgramComplete) {
          status = "Locked";
          return status;
        }
      }

      const progressCheckCompleted = ProgramUtils.isProgressCheckCompleted(
        allEvidenceAdded,
        progressCheck.Approval,
        progressCheck.KeepOpenOnCompletion ?? false,
        progressCheckData?.submissions
      );

      if (progressCheckLocked && progressCheckCompleted) {
        if (ProgramUtils.canAddEvidenceAfterProgressCheckCompletion(progressCheck)) {
          status = "Added";
          return status;
        }

        status = "Locked";
        return status;
      }

      if (progressCheckLocked) {
        status = "Locked";
        return status;
      }
    }

    if (ProgramUtils.isClinicalAdvisor(program.ID) && comp.isForMigration) {
      // Migrating from MTS and CHUB, and should be open when progress check is unlocked
      if (progressCheckLocked) {
        status = "Added";
        return status;
      } else {
        status = "Open";
        return status;
      }
    } else if (comp.isFinalReview) {
      const evidenceStatus = EvidenceUtils.hasReachedEvidenceCount(progressCheckEvidence, comp);
      if (evidenceStatus !== "NONE") {
        status = "Added";
      } else {
        // check all other comps
        const otherCompsDone = competences
          .filter((comp) => !comp.isFinalReview && !comp.isForMigration && comp["Number Evidences required"])
          .every((c) => {
            const evidenceStatus = EvidenceUtils.hasReachedEvidenceCount(
              progressCheckCycleEvidence ?? progressCheckEvidence,
              c
            );
            const learningOutcomesStatus = EvidenceUtils.hasLearningOutcomes(
              progressCheckCycleEvidence ?? progressCheckEvidence,
              c
            );

            if (
              params.progressCheckId === PROGRESS_CHECK_IDS.LAS_MTS_MENTORING ||
              params.progressCheckId === PROGRESS_CHECK_IDS.LAS_CHUB_MENTORING ||
              params.progressCheckId === PROGRESS_CHECK_IDS.LAS_CA_MENTORING ||
              params.progressCheckId === PROGRESS_CHECK_IDS.LAS_CA_COURSES
            ) {
              const noLearningOutcomes = !c["Learning Objective"]?.length;
              let noRequiredLearningOutcomes = true;
              if (c["Learning Objective"]?.length) {
                noRequiredLearningOutcomes = c["Learning Objective"].every((lo) => !lo.NumberRequired);
              }
              if (
                (evidenceStatus === "COMPLETE" && learningOutcomesStatus === "COMPLETE_REQUIRED") ||
                (evidenceStatus === "COMPLETE" && learningOutcomesStatus === "COMPLETE_ALL") ||
                (evidenceStatus === "COMPLETE" && noLearningOutcomes) ||
                (evidenceStatus === "COMPLETE" && noRequiredLearningOutcomes)
              )
                return true;
            } else {
              if (evidenceStatus === "COMPLETE") return true;
            }
            return false;
          });
        if (!otherCompsDone && !ProgramUtils.isCSM(program.ID)) {
          status = "Locked";
        }
      }
    } else if (comp.isLockedByRequiredEvidence) {
      const evidenceStatus = EvidenceUtils.hasReachedEvidenceCount(progressCheckEvidence, comp);

      if (evidenceStatus !== "NONE") {
        status = "Added";
      } else {
        const otherCompsDone = competences
          .filter((comp) => !comp.isLockedByRequiredEvidence)
          .every((c) => {
            const evidenceStatus = EvidenceUtils.hasReachedEvidenceCount(
              progressCheckCycleEvidence ?? progressCheckEvidence,
              c
            );

            if (evidenceStatus === "COMPLETE") return true;

            return false;
          });

        if (!otherCompsDone) {
          status = "Locked";
        }
      }
    } else {
      if (
        ProgramUtils.canSubmitProgressCheck(progressCheck.Approval) &&
        ProgramUtils.isProgressCheckApproved(progressCheckData?.submissions)
      ) {
        status = "Added";
      } else if (
        ProgramUtils.isNQPProgram(program.ID) &&
        ProgramUtils.isProgressCheckCompleted(
          allEvidenceAdded,
          progressCheck.Approval,
          progressCheck.KeepOpenOnCompletion ?? false,
          progressCheckData?.submissions
        )
      ) {
        status = "Added";
      } else {
        // check the current competence
        const evidenceCountStatus = EvidenceUtils.hasReachedEvidenceCount(
          progressCheckCycleEvidence ?? progressCheckEvidence,
          comp
        );
        const learningOutcomesStatus = EvidenceUtils.hasLearningOutcomes(
          progressCheckCycleEvidence ?? progressCheckEvidence,
          comp
        );

        if (evidenceCountStatus === "NONE" && learningOutcomesStatus == "NONE") {
          if (comp.FilledInBy === FilledInBy.Mentor) {
            status = "Mentor to Add";
          } else {
            if (progressCheckLocked) {
              return "Locked";
            } else {
              status = "Open";
            }
          }
        } else if (evidenceCountStatus === "COMPLETE") {
          if (comp["Number Evidences required"]) {
            // handle mandatory evidence
            const noLearningOutcomes = !comp["Learning Objective"]?.length;
            let noRequiredLearningOutcomes = true;

            if (comp["Learning Objective"]?.length && !ProgramUtils.isNQPProgram(program.ID)) {
              noRequiredLearningOutcomes = comp["Learning Objective"].every((lo) => !lo.NumberRequired);
            }
            if (
              learningOutcomesStatus === "COMPLETE_ALL" ||
              learningOutcomesStatus === "COMPLETE_REQUIRED" ||
              noLearningOutcomes ||
              noRequiredLearningOutcomes
            ) {
              status = "Added";
            } else {
              if (ProgramUtils.isNQPProgram(program.ID)) {
                status = "Open";
              } else {
                status = "In Progress";
              }
            }
          } else {
            // handle optional evidence
            status = "Added";
          }
        } else {
          if (ProgramUtils.isNQPProgram(program.ID)) {
            if (comp.FilledInBy === FilledInBy.Mentor) {
              status = "Mentor to Add";
            } else {
              status = "Open";
            }
          } else {
            status = "In Progress";
          }
        }
      }
    }

    return status;
  };

  const onProgressCheckEvidenceButtonPressed = (competence: CompetenceActivityClass): void => {
    const status = getCompetenceStatus(competence);

    // addEvidencePressed(competence);
    // return;
    const canHandleMultiplePiecesOfEvidencePerEvidenceType =
      ProgramUtils.canHandleMultiplePiecesOfEvidencePerEvidenceType(program.ID);

    if (canHandleMultiplePiecesOfEvidencePerEvidenceType) {
      const LOsHidden =
        (program.RemoveSubtitleForManuallySelectedLOs ||
          ProgramUtils.removeSubtitleForManuallySelectedLOs(program.ID)) &&
        !competence["LOs Automatic"];

      const isWellbeingActivity = competence.isWellbeingActivity;
      const statusIsLocked = status === "Locked";
      const canAddOrEditProgressCheckEvidence = ProgramUtils.canAddOrEditProgressCheckEvidence(
        progressCheckData?.submissions,
        progressCheck,
        allProgressCheckData.contents,
        evidence.contents,
        program?.ID,
        progressChecks
      );

      const learningOutcomes = competence["Learning Objective"];
      const skipCompetencePage = ProgramUtils.skipCompetencePage(program.ID, competence.Description, competence.ID);

      const canAddWellbeingActivity = isWellbeingActivity && !statusIsLocked && canAddOrEditProgressCheckEvidence;

      const goToLearningOutcomes =
        (learningOutcomes !== undefined && learningOutcomes.length > 0 && !LOsHidden) || !skipCompetencePage;

      if (isWellbeingActivity) {
        const programID = program?.ID;
        const progressCheckID = progressCheck?.ID;

        if (canAddWellbeingActivity) {
          history.push(
            `/dashboard/program/${programID}/progress-check/${progressCheckID}/wellbeing-activity/${competence.ID}`
          );
        } else if (goToLearningOutcomes) {
          history.push(`/dashboard/program/${programID}/progress-check/${progressCheckID}/comp/${competence.ID}`);
        }
      } else if (goToLearningOutcomes) {
        const programID = program?.ID;
        const progressCheckID = progressCheck?.ID;

        history.push(`/dashboard/program/${programID}/progress-check/${progressCheckID}/comp/${competence.ID}`);
      } else {
        const status = getCompetenceStatus(competence);
        const multipleAllowed = competence.MultipleAllowed;

        if (multipleAllowed) {
          const canAddEvidence = status === "Open" || status === "In Progress" || status === "Added";

          if (canAddEvidence) {
            addEvidencePressed(competence);
          }
        } else {
          const canAddEvidence = status === "Open" || status === "In Progress";

          if (canAddEvidence) {
            addEvidencePressed(competence);
          }
        }
      }
    } else {
      const status = getCompetenceStatus(competence);
      const canAddEvidence = status === "Open" || status === "In Progress";

      if (canAddEvidence) {
        addEvidencePressed(competence);
      }
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar
          mode="ios"
          className="navBar"
          style={{
            maxWidth: DataController.isWeb() ? 980 : undefined,
            height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
          }}
        >
          <IonButtons slot="start">
            <IonBackButton
              className="header-back-buttons"
              defaultHref={`/dashboard/program/${program?.ID}`}
              text={DataController.getBackIconText()}
              icon={DataController.getBackIconType()}
              style={{
                marginLeft: Capacitor.getPlatform() === "android" ? 8 : 0,
                "--icon-font-size": Capacitor.getPlatform() === "android" ? "24px" : "30px",
              }}
            />
          </IonButtons>
          <HeaderTitle>{progressCheck?.Name || ""}</HeaderTitle>
          <IonButtons slot="end">
            <IonButton
              disabled={isRefreshingProgramData}
              className="header-button"
              mode="ios"
              onClick={refreshUserProgramData}
            >
              {isRefreshingProgramData ? (
                <IonSpinner className="h-[20px] w-[44px] text-white" />
              ) : (
                <img
                  src={Icon_Refresh}
                  className="headerIcon"
                />
              )}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="page-background">
        <SubmitProgressCheckModal
          cancelPressed={() => setSubmitProgressCheckModalVisible(false)}
          submitPressed={() => submitProgressCheck()}
          visible={submitProgressCheckModalVisible}
          submitting={submitting}
          headerText={progressCheck?.SubmissionModalHeader || ""}
          bodyText={progressCheck?.SubmissionModalBody || ""}
          expand={progressCheck?.Approval === ProgressCheckApproval.ApprovalOO}
        />
        <div>
          {progressCheck && (
            <ProgramInfoCard title={progressCheck.Name}>
              {!ProgramUtils.isProgressCheckApproved(progressCheckData?.submissions) &&
                !ProgramUtils.canAddEvidenceAfterProgressCheckCompletion(progressCheck) &&
                ProgramUtils.checkIfProgressCheckIsLocked(
                  progressCheck,
                  progressChecks,
                  evidence.contents,
                  currentProgressCheckData,
                  program.ID,
                  fastTrackActive,
                  pauseAndHold
                ) && (
                  <div className="mb-2">
                    <EvidenceDisclaimer
                      message={
                        ProgramUtils.getProgressCheckPauseAndHoldLockedText(pauseAndHold) ??
                        ProgramUtils.getProgressCheckSubmissionLockedDisclaimerText(progressCheckData) ??
                        ProgramUtils.getProgressCheckLockedDisclaimerText(progressCheck.ID)
                      }
                    />
                  </div>
                )}
              {ProgramUtils.isProgressCheckApproved(progressCheckData?.submissions) && (
                <div className="mb-2 flex flex-col">
                  <ProgressCheckDescription
                    description={ProgramUtils.formatApprovalText(
                      progressCheck.SubmissionApprovedText,
                      progressCheckData?.submissions
                    )}
                  />
                  <ProgramCardButton
                    callToAction
                    buttonText={ProgramUtils.getProgressCheckSubmissionButtonText(progressCheckData?.submissions)}
                    buttonType={ProgramUtils.getProgressCheckSubmissionButtonClass(progressCheckData?.submissions)}
                    buttonTextColor={ProgramUtils.getProgressCheckSubmissionButtonTextClass(
                      progressCheckData?.submissions
                    )}
                    buttonPressed={() => console.log("")}
                    disabled={ProgramUtils.isProgressCheckButtonDisabled(
                      progressCheckData?.submissions,
                      allEvidenceAdded
                    )}
                  />
                </div>
              )}
              {ProgramUtils.isProgressCheckRejected(progressCheckData?.submissions) && (
                <div className="mb-2 flex flex-col">
                  <ProgressCheckDescription
                    description={ProgramUtils.formatRejectionText(
                      progressCheck.SubmissionRejectedText,
                      progressCheckData?.submissions
                    )}
                  />
                  <ProgramCardButton
                    callToAction
                    buttonText={ProgramUtils.getProgressCheckSubmissionButtonText(progressCheckData?.submissions)}
                    buttonType={ProgramUtils.getProgressCheckSubmissionButtonClass(progressCheckData?.submissions)}
                    buttonTextColor={ProgramUtils.getProgressCheckSubmissionButtonTextClass(
                      progressCheckData?.submissions
                    )}
                    buttonPressed={() => console.log("")}
                    disabled={ProgramUtils.isProgressCheckButtonDisabled(
                      progressCheckData?.submissions,
                      allEvidenceAdded
                    )}
                  />
                </div>
              )}
              {ProgramUtils.showApprovalStatusBannerOnProgressCheckPage(program.ID) &&
                ProgramUtils.isProgressCheckApprovedBasedOnCompTrigger(progressCheckEvidence, competences) && (
                  <div>
                    <div className="mb-3 text-acc-13px">
                      {progressCheck.Name} was approved by your{" "}
                      {progressCheck.Name === "Mentoring" ? "manager" : "tutor"} on{" "}
                      <span className="font-acc-600">{getDateOfProgressCheckApproval()}</span>
                    </div>
                    <div className="rounded-lg bg-green-background p-3 text-center text-acc-16px font-acc-600 text-green-text">
                      Approved
                    </div>
                  </div>
                )}
              {ProgramUtils.showApprovalStatusBannerOnProgressCheckPage(program.ID) &&
                !ProgramUtils.isProgressCheckApprovedBasedOnCompTrigger(progressCheckEvidence, competences) &&
                progressCheckEvidence.some((e) => {
                  const parsed = e.evidenceJSON;
                  return parsed.OnHoldReset && parsed.OnHoldReset === 1;
                }) && (
                  <div>
                    <div className="mb-3 text-acc-13px">
                      Your {progressCheck.Name} were reset by your{" "}
                      {progressCheck.Name === "Mentoring" ? "manager" : "tutor"}
                    </div>
                    <div className="rounded-lg bg-red-400 p-3 text-center text-acc-16px font-acc-600 text-white">
                      Reset
                    </div>
                  </div>
                )}
              {ProgramUtils.isProgressCheckCompleted(
                allEvidenceAdded,
                progressCheck.Approval,
                progressCheck.KeepOpenOnCompletion ?? false,
                progressCheckData?.submissions
              ) &&
                !ProgramUtils.isTelephoneTriageModule(program.ID) && (
                  <div className="mb-2 flex flex-col">
                    <ProgramCardButton
                      callToAction
                      buttonText="Completed"
                      buttonType="!program-card-button-approved"
                      buttonTextColor="!text-green-text"
                      buttonPressed={() => console.log("")}
                      disabled={true}
                    />
                  </div>
                )}
              {progressCheck.Description && <ProgressCheckDescription description={progressCheck.Description} />}

              {competences.length > 0 && (
                <div className="mt-[4px]">
                  {competences.map((competence) => {
                    if (competence.hideCompetence) {
                      return null;
                    }

                    return (
                      <ProgressCheckEvidenceButton
                        key={competence.ID}
                        competence={competence}
                        evidenceLabel={getEvidenceLabel(competence)}
                        learningOutcomesLabel={getLearningOutcomesLabel(competence)}
                        competenceStatus={getCompetenceStatus(competence)}
                        programId={program.ID}
                        progressCheckData={progressCheckData}
                        onPress={() => onProgressCheckEvidenceButtonPressed(competence)}
                      />
                    );
                  })}
                </div>
              )}

              {ProgramUtils.isMTS(program.ID) ||
              ProgramUtils.isCHUB(program.ID) ||
              ProgramUtils.isClinicalAdvisor(program.ID) ||
              ProgramUtils.isSPAPRP(program.ID) ? (
                <ProgramCardButton
                  buttonPressed={() => addEvidencePressed()}
                  buttonText="Add new evidence"
                  icon={Icon_Add_Evidence}
                  disabled={
                    ProgramUtils.checkIfProgressCheckIsLocked(
                      progressCheck,
                      progressChecks,
                      evidence.contents,
                      currentProgressCheckData,
                      program.ID,
                      fastTrackActive,
                      pauseAndHold
                    ) || ProgramUtils.isProgressCheckApprovedBasedOnCompTrigger(progressCheckEvidence, competences)
                  }
                />
              ) : (
                <ProgramCardButton
                  buttonPressed={() => addEvidencePressed()}
                  buttonText="Add new evidence"
                  icon={Icon_Add_Evidence}
                  disabled={
                    ProgramUtils.progressCheckAddNewEvidenceButtonDisabled(
                      progressCheck,
                      progressChecks,
                      evidence.contents,
                      currentProgressCheckData,
                      program.ID,
                      fastTrackActive,
                      pauseAndHold
                    ) ||
                    ProgramUtils.isAllEvidenceAddedForProgressCheck(
                      progressCheckEvidence,
                      competences,
                      progressCheck,
                      true
                    ) ||
                    ProgramUtils.isProgressCheckCompleted(
                      allEvidenceAdded,
                      progressCheck.Approval,
                      progressCheck.KeepOpenOnCompletion ?? false,
                      progressCheckData?.submissions
                    ) ||
                    ProgramUtils.isProgressCheckApproved(progressCheckData?.submissions) ||
                    ProgramUtils.isProgramCompleteNQP(
                      progressChecks,
                      allProgressCheckData.contents,
                      evidence.contents,
                      program?.ID
                    ) ||
                    ProgramUtils.checkIfMenteeCanAddEvidence(
                      progressCheck,
                      evidence.contents,
                      ProgramUtils.useNewMenteeEvidenceImplementation(program.ID)
                    ) ||
                    !ProgramUtils.canAddOrEditProgressCheckEvidence(
                      progressCheckData?.submissions,
                      progressCheck,
                      allProgressCheckData.contents,
                      evidence.contents,
                      program?.ID,
                      progressChecks
                    )
                  }
                />
              )}
            </ProgramInfoCard>
          )}
          {progressCheck?.CommentsSupported && (
            <CenterContainer>
              <CommentsCard
                buttonPressed={() => goToComments()}
                latestComment={CommentHelpers.getLatestComment(progressCheckData?.comments, program.MentorTitle)}
                showNew={CommentHelpers.showNewCommentsCardLabel(
                  user.contents,
                  progressCheckData?.comments,
                  progressCheckData?.commentReadTimes
                )}
                unreadCount={CommentHelpers.getUnreadCommentCount(
                  user.contents,
                  progressCheckData?.comments,
                  progressCheckData?.commentReadTimes
                )}
              />
            </CenterContainer>
          )}
          {progressCheck?.Approval &&
            ProgramUtils.canSubmitProgressCheck(progressCheck.Approval) &&
            !ProgramUtils.isProgressCheckApproved(progressCheckData?.submissions) &&
            !ProgramUtils.isProgressCheckRejected(progressCheckData?.submissions) && (
              <ProgramInfoCard
                title="Submit"
                subTitle={getSubmissionCardHeaderText(progressCheck, progressCheckData)}
                subTitleIsError={submissionHasError(progressCheckData)}
                sidePill={submissionHasError(progressCheckData)}
                sidePillText={getSubmissionSidePillText(progressCheckData)}
              >
                <ProgramCardButton
                  callToAction
                  buttonText={ProgramUtils.getProgressCheckSubmissionButtonText(progressCheckData?.submissions)}
                  buttonType={ProgramUtils.getProgressCheckSubmissionButtonClass(progressCheckData?.submissions)}
                  buttonTextColor={ProgramUtils.getProgressCheckSubmissionButtonTextClass(
                    progressCheckData?.submissions
                  )}
                  buttonPressed={() => submitButtonPressed()}
                  disabled={
                    ProgramUtils.isProgressCheckSubmissionButtonDisabled(
                      progressCheckData?.submissions,
                      competences,
                      progressCheckEvidence,
                      allEvidenceAdded,
                      fastTrackActive,
                      ProgramUtils.isNQPProgram(program.ID)
                    ) ||
                    (ProgramUtils.isNQPProgram(program.ID) &&
                      ProgramUtils.isProgramCompleteNQP(
                        progressChecks,
                        allProgressCheckData.contents,
                        evidence.contents,
                        program?.ID
                      ))
                  }
                />
              </ProgramInfoCard>
            )}
          {ProgramUtils.isCHUB(program.ID) &&
            progressCheck &&
            progressCheck.ID === PROGRESS_CHECK_IDS.LAS_CHUB_ONGOING_DEVELOPMENT && (
              <ProgramInfoCard
                title={"Ongoing Development"}
                subTitle={
                  "Complete a minimum of 2 CPD evidences each month - either Peer Review with Peer Review LO set or General"
                }
              >
                <MonthlyEvidenceBreakdown
                  evidence={progressCheckEvidence}
                  programId={program.ID}
                  generalCompId={COMPETENCY_IDS.LAS_CHUB_ONGOING_DEVELOPMENT_GENERAL_COMPETENCY}
                  paraPassLearningCompId={COMPETENCY_IDS.LAS_CA_ONGOING_DEVELOPMENT_PARAPASS_LEARNING_COMPETENCY}
                />
              </ProgramInfoCard>
            )}
          {ProgramUtils.isClinicalAdvisor(program.ID) &&
            progressCheck &&
            progressCheck.ID === PROGRESS_CHECK_IDS.LAS_CA_ONGOING_DEVELOPMENT && (
              <ProgramInfoCard
                title={"Ongoing Development"}
                subTitle={
                  "Complete a minimum of 2 CPD evidences each month - either Peer Review, ParaPass Learning or General CPD."
                }
              >
                <MonthlyEvidenceBreakdown
                  evidence={progressCheckEvidence}
                  programId={program.ID}
                  generalCompId={COMPETENCY_IDS.LAS_CA_ONGOING_DEVELOPMENT_GENERAL_COMPETENCY}
                  paraPassLearningCompId={COMPETENCY_IDS.LAS_CA_ONGOING_DEVELOPMENT_PARAPASS_LEARNING_COMPETENCY}
                />
              </ProgramInfoCard>
            )}
          <ProgramInfoCard
            title="Evidence list"
            subTitle={
              progressCheckEvidence.length === 0
                ? "Any evidence added for this progress check will appear below."
                : undefined
            }
          >
            {!ProgramUtils.canAddOrEditProgressCheckEvidence(
              progressCheckData?.submissions,
              progressCheck,
              allProgressCheckData.contents,
              evidence.contents,
              program?.ID,
              progressChecks
            ) &&
              !ProgramUtils.canAddEvidenceAfterProgressCheckCompletion(progressCheck) &&
              !ProgramUtils.hideDisclaimerText(program.ID) &&
              progressCheckData?.submissions.status !== SubmissionState.OnHoldReset && (
                <div>
                  <EvidenceDisclaimer
                    message={ProgramUtils.getEvidenceDisclaimerText(progressCheckData?.submissions, program.ID)}
                  />
                </div>
              )}
            {progressCheckEvidence.length > 0 && (
              <div>
                {progressCheckEvidence.map((item) => (
                  <ProgramEvidenceCard
                    key={item.id}
                    buttonPressed={() => evidenceCardPressed(item)}
                    programEvidence={item}
                    program={program}
                    progressCheckLocked={
                      ProgramUtils.isNQPProgram(program.ID) &&
                      ProgramUtils.isProgramCompleteNQP(
                        progressChecks,
                        allProgressCheckData.contents,
                        evidence.contents,
                        program?.ID
                      )
                    }
                  />
                ))}
              </div>
            )}
          </ProgramInfoCard>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ProgressCheckPage;
